/* ============================================================
   AEQUIL® Foundation Quizzes — engine
   All question text, band copy, connection copy and guardrails
   come from D, extracted verbatim from Frédéric's workbooks.
   ============================================================ */

/* ------------------------------------------------------------
   SUBMISSION ENDPOINT  
   Paste the collection URL between the quotes. While empty the
   app runs in preview mode: results stay in the browser and can
   be downloaded as CSV from the result screen.
   ------------------------------------------------------------ */
const ENDPOINT = window.AEQUIL_QUIZ_ENDPOINT || "";

const BURDEN = D.scale.burden;
const APPETITE = D.scale.appetite;
const INTENSITY = D.scale.intensity;
const PRESSURE = D.pressure;          /* below this a foundation is "under pressure" */
const BRANCH_AT = 62.5;               /* Frédéric's Short branch trigger */
const PAGE = 6;

const $  = (s,r=document)=>r.querySelector(s);
const el = (t,c,h)=>{const n=document.createElement(t); if(c)n.className=c; if(h!==undefined)n.innerHTML=h; return n;};
const esc = s => String(s==null?"":s).replace(/[&<>"]/g,m=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[m]));
const isMobile = ()=>window.matchMedia("(max-width:900px)").matches;
const q = id => D.bank[id];

const RINGS = `<svg viewBox="0 0 126 120" aria-hidden="true"><ellipse cx="52.5" cy="60" rx="51.5" ry="59" vector-effect="non-scaling-stroke"/><ellipse cx="73.5" cy="60" rx="51.5" ry="59" vector-effect="non-scaling-stroke"/></svg>`;
const ves  = on => `<span class="ves${on?" on":""}">${RINGS}</span>`;
const TICK = `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 10.5l4 4 7-8.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const box  = on => `<span class="tickbox${on?" on":""}">${TICK}</span>`;

/* ============================================================
   SCORING — exactly the rules in Frédéric's matrices
   ============================================================ */
function itemScore(id, answer){
  if(answer==null || answer==="N/A") return null;          /* excluded from the denominator */
  const sc = q(id).scale||"burden";
  const map = sc==="appetite" ? APPETITE : sc==="intensity" ? INTENSITY : BURDEN;
  const v = map[answer];
  return v===undefined ? null : v;
}
function mean(ids, A){
  const v = ids.map(i=>itemScore(i,A[i])).filter(x=>x!==null);
  return v.length ? v.reduce((a,b)=>a+b,0)/v.length : null;
}
function band(s){
  if(s==null) return null;
  const r = Math.round(s);
  const b = D.scale.bands.find(([lo,hi])=>r>=lo && r<=hi);
  return b ? b[2] : null;
}
/* Short Foundation Signals come from the fixed 42 core only.
   Adaptive answers deepen wording, connections and routing, never the number. */
function shortScores(A){
  const out={};
  D.foundations.forEach(f=>{
    const core=D.short.core.filter(c=>c.foundation===f).map(c=>c.id);
    const m=mean(core,A); if(m!==null) out[f]=m;
  });
  return out;
}
/* score every foundation from whatever has been answered — the Full Assessment */
function foundationScores(A){
  const by={};
  Object.keys(A).forEach(id=>{
    const it=q(id); if(!it || !it.scored) return;
    (by[it.foundation]=by[it.foundation]||[]).push(id);
  });
  const out={};
  Object.entries(by).forEach(([f,ids])=>{ const m=mean(ids,A); if(m!==null) out[f]=m; });
  return out;
}

/* ============================================================
   APPLICABILITY
   ============================================================ */
function applies(id, S){
  const a=(q(id).applicability||"").toLowerCase();
  if(a.startsWith("all")) return true;
  if(a.includes("male pathway")||a==="men"||a.includes("male physiology")) return S.sex==="Male";
  if(a.includes("women")||a.includes("female")) return S.sex==="Female";
  if(a.includes("night waking")) return ["Sometimes","Often","Very often"].includes(S.answers["V4Q063"]);
  if(a.includes("adult vitality")) return S.age==="40 or over";
  if(a.includes("fix question")||a.includes("after fix")) return !!S.answers["V4Q074"] && S.answers["V4Q074"]!=="Never";
  if(a.includes("reproductive history")) return S.answers["V4Q119"]==="Yes";
  if(a.includes("trying to conceive")) return S.sex==="Female";
  return true;
}

/* ============================================================
   STATE + ROUTING
   ============================================================ */
const MOUNT = document.getElementById("aequil-quiz");
const EMBED = !!MOUNT;

/* Three ways in, checked in this order.
   1. ?quiz=sleep-recovery   a link straight to one assessment on the shared page
   2. #/quiz/sleep-recovery  in-app navigation and older links
   3. data-quiz="..."        a page pinned to one assessment, if you ever split them out
   With none of them, the hub opens. */
const PARAM_QUIZ = (()=>{ try{
    const u=new URLSearchParams(location.search);
    return (u.get("quiz")||u.get("q")||"").trim().toLowerCase();
  }catch(e){ return ""; } })();
const PINNED = MOUNT ? (MOUNT.getAttribute("data-quiz")||"").trim() : "";
const FIXED_QUIZ = PINNED;

const S = {
  mode:null, quiz:null, started:new Date().toISOString(),
  responseId:"r"+Date.now().toString(36)+Math.random().toString(36).slice(2,7),
  sex:"", age:"", answers:{}, flags:[], noFlags:false,
  name:"", email:"", phone:"", contactOk:false, unlocked:false
};
let flow=[], idx=0;

let entered=false;
function route(){
  const h=(location.hash||"#/").replace(/^#/,"");
  /* on first load an entry link wins; after that the hash drives navigation */
  let want = h;
  if(h==="/" && !entered){ want = PINNED || PARAM_QUIZ || h; }
  entered = true;
  const m=String(want).match(/^\/?quiz\/([a-z0-9-]+)|^([a-z0-9-]+)$/);
  const slug = m ? (m[1]||m[2]) : null;
  if(slug){ const qz=Object.values(D.mini).find(x=>x.slug===slug); if(qz) return start("mini",qz); }
  if(/assessment\/full|^full$/.test(want)) return start("full");
  if(/^\/?assessment$|^short$/.test(want)) return start("short");
  return start("hub");
}
function goTo(target){
  const want = "#" + (target==="hub" ? "/" : target==="short" ? "/assessment"
                    : target==="full" ? "/assessment/full" : "/quiz/"+target);
  if(location.hash===want) route(); else location.hash=want;
}
function start(mode, quiz){
  S.mode=mode; S.quiz=quiz||null; S.answers={}; S.flags=[]; S.noFlags=false; S.unlocked=false;
  buildFlow(); idx=0; render();
}
function itemsFor(){
  if(S.mode==="mini") return S.quiz.items.map(i=>i.id);
  if(S.mode==="short") return D.short.core.map(c=>c.id);
  return Object.values(D.bank).filter(b=>b.source.startsWith("Foundation")).map(b=>b.id);
}
function activeItems(){ return itemsFor().filter(id=>applies(id,S)); }

/* the Short opens a foundation's branch when its core is at or under 62.5,
   or when any of its core items was answered Often / Very often */
function shortBranches(){
  const out=[];
  D.foundations.forEach(f=>{
    const core=D.short.core.filter(c=>c.foundation===f).map(c=>c.id);
    if(!core.length) return;
    const m=mean(core,S.answers);
    const hot=core.some(i=>["Often","Very often"].includes(S.answers[i]));
    if((m!==null && m<=BRANCH_AT) || hot)
      out.push(...D.short.branches.filter(b=>b.foundation===f).map(b=>b.id).filter(id=>applies(id,S)));
  });
  return out;
}
function pageRanges(n){
  const p=Math.max(1,Math.ceil(n/PAGE)), base=Math.floor(n/p), rem=n%p;
  const o=[]; let s=0;
  for(let i=0;i<p;i++){ const len=base+(i<rem?1:0); o.push([s,len]); s+=len; }
  return o;
}
function buildFlow(){
  if(S.mode==="hub"){ flow=["hub"]; return; }
  flow=["intro"];
  const needs = S.mode==="mini" ? S.quiz.needs : ["sex","age"];
  if(needs.length) flow.push("about");
  const n=activeItems().length;
  pageRanges(n).forEach((_,i)=>flow.push("q:"+i));
  if(S.mode==="short") flow.push("branch");
  flow.push("safety","score","gate","result");
}
function go(n){ idx=Math.max(0,Math.min(flow.length-1,n)); render(); $(".pane").scrollTop=0; window.scrollTo(0,0); }
const next=()=>go(idx+1), back=()=>go(idx-1);

/* ============================================================
   COMPONENTS
   ============================================================ */
function multiRow(id){
  const it=q(id), cur=(S.answers[id]||"").split("|").filter(Boolean);
  const w=el("div","multi");
  w.appendChild(el("p","multi-q",esc(it.question)));
  w.appendChild(el("p","hint","Select any that apply"));
  const g=el("div","multi-grid");
  it.options.forEach(o=>{
    const on=cur.includes(o);
    const b=el("button","mopt"+(on?" on":""), box(on)+`<span>${esc(o)}</span>`);
    b.type="button"; b.setAttribute("role","checkbox"); b.setAttribute("aria-checked",on);
    b.onclick=()=>{
      const set=new Set((S.answers[id]||"").split("|").filter(Boolean));
      set.has(o)?set.delete(o):set.add(o);
      S.answers[id]=[...set].join("|");
      const v=set.has(o); b.classList.toggle("on",v);
      b.querySelector(".tickbox").classList.toggle("on",v); b.setAttribute("aria-checked",v);
    };
    g.appendChild(b);
  });
  w.appendChild(g); return w;
}

function matrix(ids, opts={}){
  const wrap=el("div","matrix");
  ids.forEach((id,n)=>{
    const it=q(id);
    if(it.scale==="multi"){ const m=multiRow(id); m.dataset.id=id; wrap.appendChild(m); return; }
    const options=it.options.filter(o=>!/^n\/a|^i don't|prefer not/i.test(o));
    const naLabel=it.options.find(o=>/^n\/a|^i don't|prefer not/i.test(o));
    const row=el("div","m-row"); row.dataset.id=id;
    row.style.animationDelay=(50+n*40)+"ms";
    const qc=el("div","m-q"); qc.appendChild(el("p",null,esc(it.question)));
    let na=null;
    if(naLabel || opts.allowNA){
      na=el("button","na"+(S.answers[id]==="N/A"?" sel":""), naLabel? esc(naLabel) : "Not applicable");
      na.type="button"; qc.appendChild(na);
    }
    row.appendChild(qc);
    const cells=el("div","m-cells");
    cells.setAttribute("role","radiogroup"); cells.setAttribute("aria-label",it.question);
    const paint=pick=>{
      [...cells.children].forEach((x,xi)=>{
        const on=xi===pick; x.classList.toggle("sel",on); x.setAttribute("aria-checked",on);
        x.querySelector(".ves").classList.toggle("on",on);
      });
      row.classList.toggle("na-on", S.answers[id]==="N/A");
      if(na) na.classList.toggle("sel", S.answers[id]==="N/A");
      row.classList.remove("err");
    };
    options.forEach((label,ci)=>{
      const on=S.answers[id]===label;
      const c=el("button","cell"+(on?" sel":""), ves(on)+`<span class="cl">${esc(label)}</span>`);
      c.type="button"; c.setAttribute("role","radio"); c.setAttribute("aria-checked",on); c.setAttribute("aria-label",label);
      c.onclick=()=>{ S.answers[id]=label; paint(ci); };
      cells.appendChild(c);
    });
    row.appendChild(cells);
    if(na) na.onclick=()=>{ S.answers[id]= S.answers[id]==="N/A" ? null : "N/A"; paint(-1); };
    if(S.answers[id]==="N/A") row.classList.add("na-on");
    wrap.appendChild(row);
  });
  return wrap;
}
function checkMissing(wrap, ids){
  const missing=ids.filter(id=>S.answers[id]==null && q(id).scale!=="multi");
  [...wrap.querySelectorAll(".m-row")].forEach(r=>r.classList.toggle("err", S.answers[r.dataset.id]==null));
  if(!missing.length) return true;
  const row=wrap.querySelector(`.m-row[data-id="${missing[0]}"]`);
  if(row) row.scrollIntoView({behavior:"smooth",block:"center"});
  return false;
}
function chips(list,cur,on){
  const w=el("div","chips");
  list.forEach(o=>{ const b=el("button","chip"+(cur===o?" sel":""),esc(o)); b.type="button";
    b.onclick=()=>{on(o);[...w.children].forEach(c=>c.classList.toggle("sel",c.textContent===o));}; w.appendChild(b); });
  return w;
}
function checkRow(label,checked,on){
  const b=el("button","check"+(checked?" on":""), box(checked)+`<span>${esc(label)}</span>`);
  b.type="button"; b.setAttribute("role","checkbox"); b.setAttribute("aria-checked",!!checked);
  b.onclick=()=>{ const v=!b.classList.contains("on"); b.classList.toggle("on",v);
    b.querySelector(".tickbox").classList.toggle("on",v); b.setAttribute("aria-checked",v); on(v); };
  return b;
}
function actions(label,onGo,opts={}){
  const a=el("div","actions");
  const err=el("p","form-msg",opts.error||"Please answer every question above.");
  a.appendChild(err);
  const p=el("button","btn",label); p.type="button"; p.onclick=()=>onGo(err); a.appendChild(p);
  if(opts.skip){ const s=el("button","tlink",opts.skip); s.type="button"; s.onclick=opts.onSkip; a.appendChild(s); }
  return a;
}
function setMargin(sect,sub,pct,txt){
  $(".m-sect").textContent=sect||""; $(".m-sub").innerHTML=sub||"";
  $(".prog").classList.toggle("hide", pct==null);
  $(".fill").style.width=(pct||0)+"%"; $(".ptxt").textContent=txt||"";
}

/* ============================================================
   RENDER
   ============================================================ */
function render(){
  const pane=$(".pane"); pane.innerHTML="";
  const key=flow[idx], kind=key.split(":")[0], page=+(key.split(":")[1]||0);
  const screen=el("div","screen anim");
  const b=el("button","back","← Back"); b.type="button";
  b.onclick=()=> idx===0 ? (FIXED_QUIZ ? history.back() : goTo("hub")) : back();
  if(kind==="hub") b.hidden=true;
  if(idx===0 && kind!=="hub"){ b.textContent = FIXED_QUIZ ? "← Back" : "← All quizzes"; if(FIXED_QUIZ && !document.referrer) b.hidden=true; }
  pane.appendChild(b); pane.appendChild(screen);
  ({hub:sHub,intro:sIntro,about:sAbout,q:sQuestions,branch:sBranch,safety:sSafety,
    score:sScore,gate:sGate,result:sResult})[kind](screen,page);

  const host=$("#action-host"); host.innerHTML="";
  if(isMobile()){ const bar=screen.querySelector(".actions"); if(bar) host.appendChild(bar); }
}
const pct=()=>Math.round(idx/(flow.length-1)*100);

/* ---------- hub ---------- */
function sHub(s){
  setMargin("The quizzes","Frédéric Roscop<br>AEQUIL®",null,"");
  s.classList.add("top");
  s.appendChild(el("p","eyebrow", D.copy.hub_eyebrow));
  s.appendChild(el("h1",null, D.copy.hub_head));
  s.appendChild(el("p","lede", D.copy.hub_lede));

  const hero=el("div","hero");
  hero.innerHTML=`<div><h2>The Short Foundation Assessment</h2>
    <p>${esc(D.copy.short_desc)}</p>
    <p class="meta">${esc(D.copy.short_meta)}</p></div>`;
  const hb=el("button","btn","Begin"); hb.type="button"; hb.onclick=()=>goTo("short");
  hero.appendChild(hb); s.appendChild(hero);

  const g=el("div","grp");
  g.appendChild(el("div","g-head",`<h3>One-minute quizzes</h3><span>Choose the foundation on your mind</span>`));
  const grid=el("div","qgrid");
  Object.values(D.mini).forEach(qz=>{
    const c=el("button","qcard",`<h4>${esc(qz.name)}</h4><p>${esc(qz.hook)}</p><span class="t">${esc(qz.time)}</span>`);
    c.type="button"; c.onclick=()=>goTo(qz.slug); grid.appendChild(c);
  });
  g.appendChild(grid); s.appendChild(g);

  const g2=el("div","grp");
  g2.appendChild(el("div","g-head",`<h3>The Full Assessment</h3><span>For depth</span>`));
  const hero2=el("div","hero");
  hero2.innerHTML=`<div><h2>The Full Foundation Assessment</h2>
    <p>${esc(D.copy.full_desc)}</p>
    <p class="meta">${esc(D.copy.full_meta)}</p></div>`;
  const h2b=el("button","btn ghost","Begin"); h2b.type="button"; h2b.onclick=()=>goTo("full");
  hero2.appendChild(h2b); g2.appendChild(hero2); s.appendChild(g2);
}

/* ---------- intro ---------- */
function sIntro(s){
  const t = S.mode==="mini" ? S.quiz.name : S.mode==="short" ? "Short Foundation Assessment" : "Full Foundation Assessment";
  const hook = S.mode==="mini" ? S.quiz.hook
    : S.mode==="short" ? "How supported are your eighteen foundations right now?"
    : "The complete picture across all eighteen foundations.";
  const time = S.mode==="mini" ? S.quiz.time : S.mode==="short" ? "Around 4 minutes" : "Around 8 minutes";
  setMargin(t,"Before you start",null,"");
  s.appendChild(el("p","eyebrow", S.mode==="mini" ? "One-minute quiz" : "AEQUIL® Assessment"));
  s.appendChild(el("h1",null,esc(hook)));
  const L=el("div","letter"); L.style.marginTop="30px";
  L.innerHTML=`<p>Thinking about the past four weeks, choose the answer that feels closest. There are no right answers.</p>
    <p>You will get your signal straight away.</p>`;
  s.appendChild(L);
  s.appendChild(el("p","note", esc(time) + (S.mode==="mini" ? " · " + esc(S.quiz.target) : " · All 18 foundations")));
  s.appendChild(actions("Begin", ()=>next()));
}

/* ---------- sex / age ---------- */
function sAbout(s){
  const needs = S.mode==="mini" ? S.quiz.needs : ["sex","age"];
  setMargin(S.mode==="mini"?S.quiz.name:"About you","So we ask<br>the right questions",pct(),"Getting started");
  s.appendChild(el("p","eyebrow","So we ask the right questions"));
  s.appendChild(el("h1","sm","A little about you"));
  s.appendChild(el("p","note", D.copy.about_note));
  const f=el("div","fields");
  if(needs.includes("sex")){
    const a=el("div","field"); a.innerHTML=`<label>Which applies to you?<span class="req">Optional</span></label>`;
    a.appendChild(chips(["Female","Male","Prefer not to say"],S.sex,v=>S.sex=v)); f.appendChild(a);
  }
  if(needs.includes("age")){
    const b=el("div","field"); b.innerHTML=`<label>Your age<span class="req">Optional</span></label>`;
    b.appendChild(chips(["Under 40","40 or over","Prefer not to say"],S.age,v=>S.age=v)); f.appendChild(b);
  }
  s.appendChild(f);
  s.appendChild(actions("Continue", ()=>{ buildFlow(); next(); }));
}

/* ---------- questions ---------- */
function sQuestions(s,page){
  const ids=activeItems(), ranges=pageRanges(ids.length), [off,len]=ranges[page]||[0,0];
  const slice=ids.slice(off,off+len);
  const t=S.mode==="mini"?S.quiz.name:S.mode==="short"?"Short Assessment":"Full Assessment";
  setMargin(t, ranges.length>1?`Part ${page+1} of ${ranges.length}`:"Your answers", pct(), `Step ${idx} of ${flow.length-1}`);
  s.appendChild(el("p","eyebrow", esc(t)));
  s.appendChild(el("h1","sm", page===0 ? "Thinking about the<br>past four weeks" : "A few more"));
  const m=matrix(slice); s.appendChild(m);
  s.appendChild(actions("Continue", err=>{
    if(!checkMissing(m,slice)){ err.classList.add("show"); return; }
    buildFlow(); next();
  }, {error:"Please answer every question. Use the not applicable option if one does not apply to you."}));
}

/* ---------- short adaptive branches ---------- */
function sBranch(s){
  const ids=shortBranches();
  if(!ids.length){ next(); return; }
  setMargin("Short Assessment","A little more detail",pct(),`Step ${idx} of ${flow.length-1}`);
  s.appendChild(el("p","eyebrow","A little more detail"));
  s.appendChild(el("h1","sm","On the areas that<br>stood out"));
  s.appendChild(el("p","note","These clarify the picture. They do not change your scores."));
  const m=matrix(ids,{allowNA:true}); s.appendChild(m);
  s.appendChild(actions("Continue", err=>{
    if(!checkMissing(m,ids)){ err.classList.add("show"); return; }
    next();
  },{error:"Please answer every question, or mark it not applicable."}));
}

/* ---------- safety ---------- */
function sSafety(s){
  const list = S.mode==="mini"
    ? D.safety.filter(r=>S.quiz.safety.includes(r.ID))
    : D.safety;
  setMargin("Before your result","One quick check",pct(),`Step ${idx} of ${flow.length-1}`);
  s.appendChild(el("p","eyebrow","One quick check"));
  s.appendChild(el("h1","sm","Is any of this<br>true for you now?"));
  s.appendChild(el("p","note","These are not scored. They are here because some things need a person, not a questionnaire."));
  const c=el("div","checks");
  list.forEach(r=>{
    c.appendChild(checkRow(r["Safety question / trigger"], S.flags.includes(r.ID), v=>{
      const i=S.flags.indexOf(r.ID);
      if(v && i<0) S.flags.push(r.ID); if(!v && i>-1) S.flags.splice(i,1);
      if(v) S.noFlags=false;
      renderNone();
    }));
  });
  const noneWrap=el("div","checks"); noneWrap.style.marginTop="14px";
  function renderNone(){
    noneWrap.innerHTML="";
    noneWrap.appendChild(checkRow("None of these apply to me.", S.noFlags && !S.flags.length, v=>{
      S.noFlags=v; if(v){ S.flags=[]; render(); }
    }));
  }
  renderNone();
  s.appendChild(c); s.appendChild(noneWrap);
  s.appendChild(actions("See my result", err=>{
    if(!S.flags.length && !S.noFlags){ err.classList.add("show"); return; }
    next();
  },{error:"Please tick any that apply, or confirm none of them do."}));
}

/* ---------- the free score ---------- */
function scores(){ return S.mode==="short" ? shortScores(S.answers) : foundationScores(S.answers); }
function miniScore(){
  const core=S.quiz.items.filter(i=>i.role==="Core scored").map(i=>i.id);
  return mean(core,S.answers);
}
function bandRow(sc){
  if(sc==null) return S.quiz.bands[0];
  return S.quiz.bands.find(b=>{
    const [lo,hi]=b.band.replace(/[^0-9\u2013\-]/g,"").split(/[\u2013\-]/).map(Number);
    return Math.round(sc)>=lo && Math.round(sc)<=hi;
  }) || S.quiz.bands[0];
}
/* the result must stand on its own — a direct link or a refresh can land here
   without the score screen having run first */
function ensureResult(){
  if(S.mode!=="mini") return;
  if(S._score==null) S._score=miniScore();
  if(!S._row) S._row=bandRow(S._score);
}

function sScore(s){
  setMargin("Your result","Your signal",pct(),`Step ${idx} of ${flow.length-1}`);
  if(S.flags.length){ return safetyResult(s); }

  if(S.mode==="mini"){
    const sc=miniScore(), row=bandRow(sc);
    s.appendChild(el("p","eyebrow", esc(S.quiz.name)));
    s.appendChild(el("h1","sm", esc(row.headline)));
    const bsc=el("div","bigscore",`<b>${Math.round(sc)}</b><i>out of 100</i>`); s.appendChild(bsc);
    s.appendChild(el("div","rband", esc(row.label)));
    S._row=row; S._score=sc;
  }else{
    const f=scores(), ranked=Object.entries(f).sort((a,b)=>a[1]-b[1]);
    s.appendChild(el("p","eyebrow", S.mode==="short"?"Short Assessment":"Full Assessment"));
    s.appendChild(el("h1","sm","Your eighteen<br>foundation signals"));
    s.appendChild(el("p","lede","Ranked by which is asking for most support. There is no overall score, by design."));
    const rows=el("div","rows");
    ranked.forEach(([name,v])=>{
      rows.appendChild(el("div","row",
        `<i class="dot" style="opacity:${(0.2+(100-v)/100*0.8).toFixed(2)}"></i>
         <span class="nm">${esc(name)}</span><span class="bd">${esc(band(v))}</span>
         <span class="sc">${Math.round(v)}%</span>`));
    });
    s.appendChild(rows);
  }
  s.appendChild(actions("Continue", ()=>next()));
}
function safetyResult(s){
  s.appendChild(el("p","eyebrow","Before anything else"));
  s.appendChild(el("h1","sm","Please speak to<br>someone about this"));
  s.appendChild(el("p","lede","You marked something that a wellbeing questionnaire is not the right tool for. Your answers are safe, but we are not going to give you a signal instead of this."));
  D.safety.filter(r=>S.flags.includes(r.ID)).forEach(r=>{
    const a=el("div","alert");
    a.innerHTML=`<h4>${esc(r.Action)}</h4><p>${esc(r["Safety question / trigger"])}</p>`;
    s.appendChild(a);
  });
  const a=el("div","alert"); a.style.borderColor="var(--neutral)"; a.style.background="var(--warm)";
  a.innerHTML=`<h4>In the UK</h4><p>Call 999 for an emergency, or 111 for urgent advice that is not an emergency. If you are struggling with how you feel, Samaritans are on 116 123, free, at any hour.</p>`;
  s.appendChild(a);
  s.appendChild(actions("Back to the quizzes", ()=>goTo("hub")));
}

/* ---------- the gate ---------- */
function sGate(s){
  if(S.flags.length){ next(); return; }
  setMargin("Your full reading","A few details",pct(),`Step ${idx} of ${flow.length-1}`);
  s.appendChild(el("p","eyebrow","Your full reading"));
  s.appendChild(el("h1","sm","Where would you like<br>us to send it?"));

  const L=el("div","locked");
  L.innerHTML=`<h3>What you unlock</h3>
    <ul><li>What your answers show, in full</li>
    <li>How your foundations connect to one another</li>
    <li>Where balance begins, and what supports it</li>
    <li>A link to come back to in four weeks and compare</li></ul>`;
  s.appendChild(L);

  const f=el("div","fields");
  const fe=el("div","field");
  fe.innerHTML=`<label for="em">Email address<span class="req">Required</span></label>
    <input id="em" type="email" inputmode="email" autocomplete="email" placeholder="you@example.com" value="${esc(S.email)}">`;
  const fn=el("div","field");
  fn.innerHTML=`<label for="nm">Your name<span class="req">Required</span></label>
    <input id="nm" type="text" autocomplete="name" value="${esc(S.name)}">`;
  const fp=el("div","field");
  fp.innerHTML=`<label for="ph">Phone<span class="req">Optional</span></label>
    <input id="ph" type="tel" inputmode="tel" autocomplete="tel" value="${esc(S.phone)}">`;
  f.appendChild(fe); f.appendChild(fn); f.appendChild(fp); s.appendChild(f);

  const c=el("div","checks");
  c.appendChild(checkRow("Frédéric and the AEQUIL® team may contact me about my result.", S.contactOk, v=>S.contactOk=v));
  s.appendChild(c);

  s.appendChild(actions("Show my full reading", err=>{
    S.email=$("#em").value.trim(); S.name=$("#nm").value.trim(); S.phone=$("#ph").value.trim();
    const ok=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(S.email) && S.name.length>1;
    fe.classList.toggle("err",!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(S.email));
    fn.classList.toggle("err",S.name.length<2);
    if(!ok){ err.classList.add("show"); return; }
    S.unlocked=true; submit(); next();
  },{error:"Please add your name and a valid email address."}));
}

/* ============================================================
   RESULT
   ============================================================ */
const MACRO=["Brain & Mental Clarity","Heart & Circulation","Digestive","Urogenital & Sexual Wellbeing"];
const GEO=[
 {f:"Brain & Mental Clarity",      short:"Brain",      cx:500,    cy:382, lx:765.5, ly:271, a:"start", nx:500, ny:352, ld:"M 562.1 308.6 L 562.1 268.4 L 738.9 268.4"},
 {f:"Heart & Circulation",         short:"Heart",      cx:610.75, cy:500, lx:884.5, ly:463, a:"start", nx:655, ny:512, ld:"M 680.8 460.9 L 857.6 460.9"},
 {f:"Urogenital & Sexual Wellbeing",short:"Urogenital",cx:500,    cy:618, lx:263,   ly:741, a:"end",   nx:500, ny:665, ld:"M 449.5 694.5 L 449.5 734.7 L 272.8 734.7"},
 {f:"Digestive",                   short:"Digestive",  cx:389.25, cy:500, lx:145,   ly:558, a:"end",   nx:345, ny:512, ld:"M 154.1 551.9 L 330.9 551.9"}
];
function petals(f){
  const svg=el("div","stage");
  let p="",l="";
  GEO.forEach((g,i)=>{
    const v=f[g.f], w=v==null?0:(100-v)/100;
    p+=`<g class="petal" style="animation-delay:${120+i*130}ms">
      <ellipse class="pf" cx="${g.cx}" cy="${g.cy}" rx="97.1" ry="110.25" fill="var(--blue)" fill-opacity="${(0.015+Math.pow(w,1.25)*0.30).toFixed(3)}"/>
      <ellipse class="pe" cx="${g.cx}" cy="${g.cy}" rx="97.1" ry="110.25" stroke-opacity="${(0.16+w*0.80).toFixed(3)}"/></g>`;
    const hot=v!=null && v<PRESSURE;
    l+=`<g style="animation-delay:${680+i*90}ms"><path class="leader" d="${g.ld}"/></g>
      <text class="plbl${hot?" hot":""}" x="${g.lx}" y="${g.ly}" text-anchor="${g.a}" style="animation-delay:${680+i*90}ms">${g.short}</text>
      <text class="pnum" x="${g.nx}" y="${g.ny}" text-anchor="middle" style="animation-delay:${760+i*90}ms">${v==null?"–":Math.round(v)+"%"}</text>`;
  });
  const hotN=GEO.filter(g=>f[g.f]!=null && f[g.f]<PRESSURE).length;
  const st=hotN>=2?1:0.22, fo=hotN>=2?(0.05+hotN*0.03).toFixed(3):0;
  svg.innerHTML=`<svg viewBox="0 244 1010 512" class="run"><g>${p}</g>
    <g class="mark" style="animation-delay:1080ms">
      <ellipse cx="480.93" cy="500" rx="97.1" ry="110.25" stroke-opacity="${st}" fill="var(--blue)" fill-opacity="${fo}"/>
      <ellipse cx="519.07" cy="500" rx="97.1" ry="110.25" stroke-opacity="${st}" fill="var(--blue)" fill-opacity="${fo}"/></g>
    <g>${l}</g></svg>`;
  return svg;
}
function firedConnections(f){
  return D.connections.filter(c=>{
    const m=c.members.filter(x=>f[x]!=null);
    if(c.kind==="macro") return MACRO.filter(x=>f[x]!=null && f[x]<PRESSURE).length>=2;
    return m.length===c.members.length && m.every(x=>f[x]<PRESSURE);
  });
}
/* Reset is the foundational recommendation. Patch + Sound+ are an additional
   layer for Sleep / Energy / Airways / Mood only. DWP personalises around it. */
function resetRoute(f){
  const H=D.hierarchy;
  const over40 = S.age==="40 or over";
  const uro=f["Urogenital & Sexual Wellbeing"], hor=f["Hormonal & Metabolic Balance"];
  const convergent = [uro,hor].filter(v=>v!=null && v<PRESSURE).length>=2;
  if(S.sex==="Female" && (over40 || convergent)) return H.reset_routes.women;
  if(S.sex==="Male"   && (over40 || convergent)) return H.reset_routes.men;
  return H.reset_routes.standard;
}
function targetedFor(f){
  const H=D.hierarchy, out=[];
  Object.entries(H.targeted).forEach(([found,items])=>{
    if(f[found]!=null && f[found] < H.strong_at) out.push({foundation:found, items});
  });
  return out.sort((a,b)=>f[a.foundation]-f[b.foundation]).slice(0,2);
}
function dwpFor(f, bandDwp){
  const H=D.hierarchy;
  const hotMacro = MACRO.filter(m=>f[m]!=null && f[m]<PRESSURE).length;
  const ranked = Object.entries(f).filter(([,v])=>v<H.strong_at).sort((a,b)=>a[1]-b[1]);
  const top = ranked.length ? ranked[0][0] : null;
  const live = m => H.dwp_available[m.split(" ")[0]] !== false;

  let primary=null;
  if(hotMacro>=2) primary="Heal+";
  else if(top && H.dwp_domain["Sound+"].includes(top)) primary="Sound+";
  else if(top && H.dwp_domain["Move+"].includes(top)) primary="Move+";
  else if(top && H.dwp_domain["Breathe+"].includes(top)) primary="Breathe+";
  else primary="Mind+";
  if(!live(primary)) primary="Mind+";

  /* secondaries come from Frédéric's own DWP emphasis for this result */
  const listed=(bandDwp||"").split("·").map(x=>x.trim()).filter(Boolean);
  const sec=[];
  listed.forEach(m=>{ const base=m.split(" ")[0];
    if(base!==primary && live(m) && sec.length<2 && !sec.includes(m)) sec.push(m); });
  ranked.slice(0,3).forEach(([n])=>{
    Object.entries(H.dwp_domain).forEach(([m,doms])=>{
      if(m!==primary && doms.includes(n) && live(m) && sec.length<2 && !sec.includes(m)) sec.push(m); });
  });
  return {primary, secondary:sec};
}

function hierarchyBlock(f, bandDwp){
  const P=D.products, H=D.hierarchy;
  const strongAll = Object.values(f).every(v=>v>=H.strong_at);
  const route=resetRoute(f), targeted=targetedFor(f), dwp=dwpFor(f,bandDwp);
  const w=el("div");

  w.appendChild(el("div","pbox lead",
    `<span class="tier">Foundational</span>
     <h4>${esc(P.reset.name)} · ${esc(route)}</h4>
     <p>${strongAll
        ? "Your foundations are holding well. Reset is worth considering proactively rather than correctively, to keep the pattern where it is."
        : "The foundational recommendation for the underlying pattern across Body · Mind · Energy."}</p>
     <a class="go" href="${P.reset.url}" target="_blank" rel="noopener">View the Reset System</a>`));

  if(targeted.length){
    const t=el("div","tier-group");
    t.appendChild(el("p","tier-lab","Additional direct support, alongside Reset"));
    const g=el("div","prod");
    targeted.forEach(x=>{
      g.appendChild(el("div","pbox",
        `<h4>${esc(x.items[0])} · ${esc(x.items[1])}</h4>
         <p>Because ${esc(x.foundation)} is standing out within the wider pattern.</p>
         <a class="go" href="${P.patch.url}" target="_blank" rel="noopener">View</a>`));
    });
    t.appendChild(g); w.appendChild(t);
  }

  const d=el("div","tier-group");
  d.appendChild(el("p","tier-lab","Ongoing personalised support"));
  d.appendChild(el("div","pbox",
    `<span class="tier">Start here</span>
     <h4>${esc(dwp.primary)}</h4>
     <p>Your Digital Wellbeing Platform starting point${dwp.secondary.length
        ? `, with ${esc(dwp.secondary.join(" and "))} to follow.` : "."}</p>
     <a class="go" href="${P.sound.url}" target="_blank" rel="noopener">Open the platform</a>`));
  w.appendChild(d);
  return w;
}

function productBlock(){
  const p=D.products;
  const w=el("div","prod");
  [p.reset,p.patch,p.sound,p.journey].forEach(x=>{
    const bx=el("div","pbox",
      `<h4>${esc(x.name)}</h4><p>${esc(x.blurb)}</p>
       <a class="go" href="${x.url}" target="_blank" rel="noopener">View</a>`);
    bx.setAttribute("data-url", x.url);
    w.appendChild(bx);
  });
  return w;
}
function sResult(s){
  ensureResult();
  setMargin("Your full reading","Complete",100,"Complete");
  s.classList.add("top");
  const f = S.mode==="mini" ? {[S.quiz.target]: S._score} : scores();
  const reading = el("div");

  if(S.mode==="mini"){
    const r=S._row;
    if(!r){ goTo("hub"); return; }
    s.appendChild(el("p","eyebrow", esc(S.quiz.name)));
    s.appendChild(el("h1","sm", esc(r.headline)));
    s.appendChild(el("div","bigscore",`<b>${Math.round(S._score)}</b><i>out of 100 · ${esc(r.label)}</i>`));
    const c=el("div","sec"); c.innerHTML=`<h3>What your answers show</h3>`;
    c.appendChild(el("div","card",`<p>${esc(r.copy)}</p>`)); reading.appendChild(c);
    const w=el("div","sec"); w.innerHTML=`<h3>What Frédéric suggests</h3>`;
    w.appendChild(el("div","card",`<p>${esc(r.recommendation)}</p>
      <p>${esc(r.targeted)}</p><p class="dwp">${esc(r.dwp)}</p>`)); reading.appendChild(w);
    const rr=el("div","sec"); rr.innerHTML=`<h3>Come back in four weeks</h3>`;
    rr.appendChild(el("div","card",`<p>${esc(r.reassessment)}</p>`)); reading.appendChild(rr);
  }else{
    s.appendChild(el("p","eyebrow", S.mode==="short"?"Short Assessment":"Full Assessment"));
    s.appendChild(el("h1","sm","Where your<br>foundations meet"));
    const hot=MACRO.filter(m=>f[m]!=null && f[m]<PRESSURE).length;
    s.appendChild(el("p","lede", hot>=2
      ? "Several of your four macroscopic foundations are asking for support together. Where they overlap is where AEQUIL® begins."
      : hot===1 ? "One of your four macroscopic foundations is asking for more support at the moment."
      : "Your four macroscopic foundations look generally supported right now."));
    s.appendChild(petals(f));

    const ranked=Object.entries(f).sort((a,b)=>a[1]-b[1]);
    const rs=el("div","sec"); rs.innerHTML=`<h3>All eighteen signals</h3>`;
    const rows=el("div","rows");
    ranked.forEach(([n,v])=>rows.appendChild(el("div","row",
      `<i class="dot" style="opacity:${(0.2+(100-v)/100*0.8).toFixed(2)}"></i>
       <span class="nm">${esc(n)}</span><span class="bd">${esc(band(v))}</span><span class="sc">${Math.round(v)}%</span>`)));
    rs.appendChild(rows); reading.appendChild(rs);

    const out = S.mode==="short" ? D.short.output : D.full.output;
    const key = S.mode==="short" ? "Short-form label" : "Internal label";
    const low=ranked.filter(([,v])=>v<D.hierarchy.strong_at).slice(0,5);
    S._dwp=(low[0]?(out.find(o=>o.Foundation===low[0][0])||{})["DWP emphasis"]:"")||"";
    if(low.length){
      const ws=el("div","sec"); ws.innerHTML=`<h3>Your priorities</h3>`;
      low.forEach(([n,v])=>{
        const row=out.find(o=>o.Foundation===n && (()=>{ const [lo,hi]=o["Score band"].replace(/[^0-9–\-]/g,"").split(/[–\-]/).map(Number);
          return Math.round(v)>=lo && Math.round(v)<=hi; })());
        if(!row) return;
        ws.appendChild(el("div","card",
          `<h4>${esc(n)} · ${Math.round(v)}%</h4><p>${esc(row["Consumer headline"])}</p>
           <p>${esc(row["Consumer copy"])}</p><p class="dwp">${esc(row["DWP emphasis"])}</p>`));
      });
      reading.appendChild(ws);
    }
    const fired=firedConnections(f);
    const cs=el("div","sec"); cs.innerHTML=`<h3>How they connect</h3>`;
    if(fired.length) fired.slice(0,3).forEach(c=>cs.appendChild(el("div","card"+(c.kind==="macro"?" macro":""),
      `<h4>${esc(c.key.replace("↔","and"))}</h4><p>${esc(c.copy)}</p><p class="dwp">${esc(c.dwp)}</p>`)));
    else cs.appendChild(el("div","card",`<p>No connected pattern is showing at the moment. Each foundation is read on its own.</p>`));
    reading.appendChild(cs);
  }

  const bandDwp = S.mode==="mini" ? (S._row && S._row.dwp) : (S._dwp||"");
  const ps=el("div","sec prods"); ps.innerHTML=`<h3>Where balance begins</h3>`;
  ps.appendChild(hierarchyBlock(f, bandDwp));
  s.appendChild(ps);
  s.appendChild(reading);

  const a=el("div","actions noprint");
  const pdf=el("button","btn","Save as PDF"); pdf.type="button"; pdf.onclick=()=>window.print();
  a.appendChild(pdf);
  const h=el("button","btn ghost", FIXED_QUIZ ? "Start again" : "Take another quiz"); h.type="button";
  h.onclick=()=> FIXED_QUIZ ? route() : goTo("hub");
  a.appendChild(h);
  if(!ENDPOINT){ const d=el("button","tlink","Download my results (CSV)"); d.type="button"; d.onclick=downloadCSV; a.appendChild(d); }
  s.appendChild(a);
}

/* ============================================================
   OUTPUT
   ============================================================ */
function payload(){
  const f = S.mode==="mini" ? {[S.quiz.target]: miniScore()} : scores();
  return {
    respondent:{response_id:S.responseId, submitted_at:new Date().toISOString(),
      instrument:S.mode, quiz_id:S.quiz?S.quiz.id:"", quiz_name:S.quiz?S.quiz.name:"",
      name:S.name, email:S.email, phone:S.phone, contact_ok:S.contactOk,
      sex:S.sex, age:S.age, safety_flags:S.flags.join("|"),
      device:isMobile()?"mobile":"desktop"},
    scores:Object.entries(f).map(([k,v])=>({response_id:S.responseId,foundation:k,
      score:v==null?"":Math.round(v*10)/10,band:band(v)||""})),
    answers:Object.entries(S.answers).filter(([,v])=>v!=null).map(([id,v])=>({
      response_id:S.responseId,item_id:id,foundation:q(id).foundation,
      question:q(id).question,answer:v,score:itemScore(id,v)}))
  };
}
function submit(){
  const d=payload();
  try{ localStorage.setItem("aequil_quiz_last",JSON.stringify(d)); }catch(e){}
  if(!ENDPOINT){ console.log("AEQUIL quiz result",d); return; }
  fetch(ENDPOINT,{method:"POST",headers:{"Content-Type":"text/plain;charset=utf-8"},body:JSON.stringify(d)})
    .catch(e=>console.error("AEQUIL quiz submit failed",e));
}
function downloadCSV(){
  const d=payload(), qt=v=>`"${String(v??"").replace(/"/g,'""')}"`;
  const sec=(title,rows)=>{ if(!rows.length) return [title,""];
    const h=Object.keys(rows[0]); return [title,h.join(","),...rows.map(r=>h.map(k=>qt(r[k])).join(",")),""]; };
  const csv=[...sec("RESPONDENT",[d.respondent]),...sec("FOUNDATION SCORES",d.scores),...sec("ANSWERS",d.answers)].join("\n");
  const a=document.createElement("a");
  a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
  a.download="aequil-result.csv"; a.click();
}

/* ============================================================
   BOOT
   ============================================================ */
if(EMBED){
  document.body.classList.add("aequil-embed");
  MOUNT.appendChild(document.querySelector(".shell"));
  MOUNT.appendChild(document.getElementById("action-host"));
}

let wasMobile=isMobile();
window.addEventListener("resize",()=>{ const n=isMobile(); if(n!==wasMobile){ wasMobile=n; render(); } });
window.addEventListener("hashchange",route);
$(".mk").onclick=()=>location.hash="#/";
route();
